import React, { useEffect, useState, useMemo } from "react";
import { ClearRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function PopupFilteringCallQueuePost({ data, filterValues, handlePostChange, onSubmit, onCancel, people, user, callQueue }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [postsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredPosts, setFilteredPosts] = useState([]);
    const { t } = useTranslation();

    const reverseCallQueue = useMemo(() => {
        const map = {};
        Object.entries(callQueue || {}).forEach(([name, number]) => {
            map[number] = name;
        });
        return map;
    }, [callQueue]);

    const uniqueEntries = useMemo(() => {
        const flatPeople = people.flat();
        const flatData = data.flat(Infinity);
        const canUseCallerId = user && user.holdingId === 33;
        const allowedNumbers = canUseCallerId ? new Set(Object.values(callQueue)) : null;
        const results = [];
        const seenNumbers = new Set();
        const agentActivityMap = new Map();

        flatData.forEach(item => {
            const numbersInCall = [item.callingnumber, item.callednumber];

            numbersInCall.forEach(num => {
                if (!num) return;

                if (!agentActivityMap.has(num)) {
                    agentActivityMap.set(num, new Set());
                }

                if (item.calleridnumber) {
                    agentActivityMap.get(num).add(JSON.stringify({ id: item.calleridnumber, isCid: true }));
                } else {
                    agentActivityMap.get(num).add(JSON.stringify({ id: num, isCid: false }));
                }
            });
        });

        flatPeople.forEach(person => {
            const personNumbers = [person.phone.work, person.phone.mobile].filter(n => n);

            personNumbers.forEach(pNumber => {
                if (agentActivityMap.has(pNumber)) {

                    const identities = agentActivityMap.get(pNumber);

                    identities.forEach(jsonIdentity => {
                        const { id, isCid } = JSON.parse(jsonIdentity);
                        const isCallerId = canUseCallerId && isCid;
                        const numberToDisplay = isCallerId ? id : pNumber;

                        if (canUseCallerId && !allowedNumbers.has(numberToDisplay)) return;

                        if (!seenNumbers.has(numberToDisplay)) {
                            seenNumbers.add(numberToDisplay);
                            
                            let computedName = '';
                            if (canUseCallerId) {
                                const baseName = reverseCallQueue[numberToDisplay] || '';
                                computedName = isCallerId ? `${baseName} ⛔` : baseName;
                            } else {
                                const firstName = person?.firstName || '';
                                const lastName = person?.lastName || '';
                                computedName = `${firstName} ${lastName}`.trim();
                            }

                            results.push({
                                displayNumber: numberToDisplay,
                                person,
                                originalMatch: pNumber,
                                isCallerId,
                                name: computedName
                            });
                        }
                    });
                }
            });
        });

        return results.sort((a, b) => {
            const nameA = a.name || "";
            const nameB = b.name || "";
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });

    }, [people, data, user, callQueue, reverseCallQueue]);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredPosts(uniqueEntries);
        } else {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            const filtered = uniqueEntries.filter(entry => {
                const { displayNumber, name } = entry;
                return (
                    displayNumber.includes(searchTerm) ||
                    (name && name.toLowerCase().includes(lowerCaseSearchTerm))
                );
            });
            setFilteredPosts(filtered);
        }
        setCurrentPage(1);
    }, [searchTerm, uniqueEntries]);

    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const handleResetSearch = () => setSearchTerm("");
    const handleOK = () => onSubmit(filterValues.post);
    const handleCancel = () => onCancel();

    const formatDisplay = (number) => number;

    return (
        <div className="popup-filtering-report-webex-statistics">
            <h2>{t("Sélection des valeurs")}</h2>
            <div className="container-search-bar-filtering-report-webex">
                <div>
                    {t("Filtrer par :")}
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button type="button" onClick={handleResetSearch}><ClearRounded /></button>
                </div>
            </div>
            <table>
                <thead>
                    <tr>
                        <th></th>
                        <th>{t("Numéro")}</th>
                        <th>{t("Nom")}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentPosts.map((entry, index) => {
                        const { displayNumber, name } = entry;

                        return (
                            <tr key={`${displayNumber}-${index}`}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={filterValues.post.includes(displayNumber)}
                                        onChange={() => handlePostChange(displayNumber)}
                                    />
                                </td>
                                <td>{formatDisplay(displayNumber)}</td>
                                {/* On utilise le nom calculé dans useMemo */}
                                <td>{name || 'N/A'}</td> 
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {filteredPosts.length > 10 && (
                <div className="pagination">
                    {currentPage > 1 && (
                        <button type="button" onClick={() => paginate(currentPage - 1)}>
                            {t("Précédent")}
                        </button>
                    )}
                    {[...Array(Math.ceil(filteredPosts.length / postsPerPage)).keys()]
                        .slice(Math.max(currentPage - 5, 0), currentPage + 5)
                        .map(number => (
                            <button type="button" key={number} onClick={() => paginate(number + 1)}>
                                {number + 1}
                            </button>
                        ))
                    }
                    {currentPage < Math.ceil(filteredPosts.length / postsPerPage) && (
                        <button type="button" onClick={() => paginate(currentPage + 1)}>
                            {t("Suivant")}
                        </button>
                    )}
                </div>
            )}
            <div className="container-buttons-popup-filtering-report-webex">
                <button type="button" onClick={handleOK}>{t("OK")}</button>
                <button type="button" onClick={handleCancel}>{t("Annuler")}</button>
            </div>
        </div>
    );
};